import { 
  getBarHexColors, 
  getBarColorsForHighcharts, 
  gpsChartxAxisLabels, 
  filterTrainingData, 
  filterChartDataByDateRange, 
  extractGameDates,
  getXAxisLabelRotation
} from '../../Utils/chart_utils';
import { trainingAPI } from '../../api/TrainingAPI';
import { drilldownPlayerLabel } from '../drilldownPlayerLabel.js';
import { healthyMean, isInjured } from '../../Utils/injury.mjs';
import { chartExportableOptions } from "../mapperUtil"
function getContrastTextColor(backgroundColor) {

  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  return brightness < 128 ? '#FFFFFF' : '#000000';
}

export function mapDataForHighchartsGroupedBar(props) {
  const {
    dataIn,
    games = [],
    metrics,
    teamColors = null,
    isComparison = false,
    showGameDays = false,
    startDate = null,
    endDate = null,
    isTeamChart = false,
    isDrillData = false,
    isMetricComparisonChart = false,
    title = "",
    showTitle = true,
    enableDrillDown = false,
    dataColorUtil = undefined,
    session = [], 
    playerGroup = [],
    position = [],
    selectedPlayerId = null
  } = props;
  const injuredInGroup = selectedPlayerId ? () => false : isInjured;
  
  if (!dataIn || !dataIn.data || metrics.length === 0) {
    return null;
  }
  
  const gameDates = extractGameDates(dataIn);
  const filteredData = filterTrainingData(dataIn, showGameDays, gameDates);
  const dateFilteredData = filterChartDataByDateRange(filteredData.data, startDate, endDate);
  const color_100 = filteredData.colors || {};

  const metricColors = {
    aerobic_training_effect: teamColors?.primary || '#000000',
    anaerobic_training_effect: teamColors?.secondary || '#888888',
  };

  const processedData = dateFilteredData.map((item) => ({
    ...item,
    opponent: games?.find((x) => x.date === item.date)?.opponent || '',
  }));

  const categories = createCategories(props, dataIn, processedData);
  const series = createSeries(props, categories, processedData, filteredData, metricColors, color_100, dataIn, enableDrillDown);
  const options = createChartOptions(props, filteredData, categories, series, showTitle, enableDrillDown, true, selectedPlayerId);


  function createSeries(props, categories, processedData, filteredData, metricColors, color_100, dataIn, enableDrillDown = false) {
    const { metrics, isComparison, teamColors, isTeamChart, isMetricComparisonChart } = props;

    if(isTeamChart || isMetricComparisonChart) {
      return metrics?.map( (metric, index) => {
        return {
          name: metric?.name,
          color: teamColors[index],
          data: processedData.map((item, dataIndex) => ({
            name: categories[dataIndex],
            y: item[metric?.key] || 0,
            color: teamColors[index],
            custom: { injured: injuredInGroup(item) },
            ...(enableDrillDown && {drilldown: `${metric.key}`})
          })),
          marker: index === 0 ? {
            lineColor: "#000000",
            lineWidth: 2
          } : {}
        };
      })
    }

    const number_of_comparisons = dataIn.num_games;
    const numberOfComparisons = Array.from({ length: number_of_comparisons }, (_, i) => i);
    const allSeries = [];

    for (let index = 0; index < numberOfComparisons.length; index++) {
      const metricKey = metrics[index];
      const metric = filteredData.metrics?.find(m => m.key === metricKey);

      const name = isComparison ? processedData[0][`${index}_opponent_name`] : metric ? metric.name : metricKey;
      if (name === undefined) continue;
      const series = {
        name,
        color: isComparison ? teamColors.all[index] : metricColors[metricKey] || `#${Math.floor(Math.random()*16777215).toString(16)}`,
        data: processedData.map((item, dataIndex) => ({
          y: item[metricKey] || 0,
          color: isComparison ? teamColors.all[index] : getBarHexColors(item, metricKey, false, color_100),
          ...(enableDrillDown && {drilldown: `group-${item.date}-${dataIndex}`})
        })),
        marker: index === 0 ? {
          lineColor: "#000000",
          lineWidth: 2
        } : {}
      };

      allSeries.push(series)

    }

    return allSeries;
  }

  function createCategories(props, dataIn, processedData) {
    const { isComparison, isTeamChart } = props;

    if (isComparison) {
      return Array.from({ length: dataIn.data.length }, (_, i) => i + 1);
    } else {
      return gpsChartxAxisLabels(processedData, isTeamChart);
    }
  }

  function createChartOptions(props, filteredData, categories, series, showTitle = true, enableDrillDown = false, showHamburger = true, selectedPlayerId = null) {
    const { metrics, isComparison, isTeamChart, title, isDrillData } = props;

    const chartTitle = showTitle ? (title || (isComparison ? '' : filteredData.title || 'Training Effects')) : '';

    let colors = {}
    let positionsArray = []
    const baseOptions = {
      chart: {
        type: 'column',
        zoomType: 'xy',
        events: {
          load: function() {
            this.isDrilling = false;
            this.options.custom = this.options.custom || {};
            this.options.custom.title = isTeamChart ? '' : chartTitle;
          },
          drilldown: function(e) {
            if (this.isDrilling) return;
            this.isDrilling = true;
            this.options.custom = this.options.custom || {};
            if (!e.seriesOptions) {
              const chart = this;
              const date = props.dataIn.data[e.point.index]?.date;
              const index = e.point.index;

              if (!chart.drilldownLevel) {
                chart.drilldownLevel = 0;
              }

              if (chart.drilldownLevel >= 2) {
                return; 
              }

              if (!chart.drilldownData) {
                chart.showLoading('Loading data ...');
                trainingAPI.getDrillDownChartData({ 
                  date,
                  position,
                  player_group: playerGroup,
                  sessions: session,
                  player_id: selectedPlayerId || null
                })
                  .then(response => {
                    const data = response.player_data;
                    colors = response.colors;
                    chart.drilldownData = data;
                    const {drilldowns, positions} = createDrilldownSeries(data, e.point.name, index, metrics, colors, enableDrillDown);
                    positionsArray = [...positions]

                    const xAxis = chart.xAxis[0];
                    xAxis.categories = positions;
                    this.options.custom.currentCategories = positions;
                    this.options.custom.drilldown = "positional";

                    chart.hideLoading();

                    chart.addSingleSeriesAsDrilldown(e.point, drilldowns[series[0].data[0].drilldown]);
                    chart.addSingleSeriesAsDrilldown(e.point, drilldowns[series[1].data[0].drilldown]);

                    chart.applyDrilldown()

                    xAxis.setCategories(positions, false);
                    getXAxisLabelRotation(this, positions);
                  })
                  .catch(error => {
                    console.error('Error fetching drill-down data:', error);
                    chart.hideLoading();
                  })
                  .finally(() => {
                    this.isDrilling = false;
                  });
              } else {
                const {drilldowns, names} = createDrilldownSeries(chart.drilldownData, e.point.name, index, metrics, colors);

                const groupOne = `${e.point.name} Players - ${series[0]?.data[0]?.drilldown}`
                const drilldownOne = drilldowns[groupOne]
                const groupTwo = `${e.point.name} Players - ${series[1]?.data[0]?.drilldown}`
                const drilldownTwo = drilldowns[groupTwo]

                const xAxis = chart.xAxis[0];
                xAxis.categories = names;
                this.options.custom.currentCategories = xAxis.categories;
                this.options.custom.drilldown = "player";
                chart.addSingleSeriesAsDrilldown(e.point, drilldownOne);
                chart.addSingleSeriesAsDrilldown(e.point, drilldownTwo);

                chart.applyDrilldown();

                xAxis.setCategories(names, false);
                getXAxisLabelRotation(this, names);

                this.isDrilling = false;
              }

              chart.drilldownLevel++
            }
          },
          drillup: function(e) {
            if (this.isDrilling) return;
            this.isDrilling = true;
            this.options.custom = this.options.custom || {};
            const chart = this;
            chart.drilldownLevel--;

            try {
              if (e.seriesOptions._levelNumber === 1) {
                const xAxis = chart.xAxis[0];
                
                xAxis.categories = positionsArray;
                this.options.custom.currentCategories = positionsArray;
                this.options.custom.drilldown = "positional";

                xAxis.setCategories(positionsArray, false);
                getXAxisLabelRotation(this, positionsArray);
              }
              if (e.seriesOptions._levelNumber === 0) {
                if (chart.drilldownData) delete chart.drilldownData;
                const xAxis = chart.xAxis[0];
                xAxis.categories = categories;
                this.options.custom.currentCategories = categories;
                this.options.custom.drilldown = "";

                xAxis.setCategories(categories, false);
                getXAxisLabelRotation(this, categories);
              }
            } finally {
              this.isDrilling = false;
            }
          }
        }
      },
      title: { text: (isTeamChart && !isDrillData) ? '' : chartTitle },
      xAxis: {
        categories,
        labels: categories.length > 20
        ? {
            rotation: -90,
            overflow: 'justify',
            staggerLines: 1,
            step: 0
          }
        : categories.length > 10
        ? {
            rotation: -45,
            overflow: 'justify',
            staggerLines: 1,
            step: 0
          }
        : {
            rotation: 0
        },
        tickmarkPlacement: categories.length > 10 ? 'on' : 'between',
        gridLineWidth: 2,
        lineWidth: 1, 
        crosshair: true,
        tickWidth: 1,
      },
      yAxis: {
        min: 0,
        gridLineWidth: 2,
        title: { text: null }
      },
      tooltip: {
        headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
        pointFormat: '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
          '<td style="padding:0"><b>{point.y:.1f}</b></td></tr>',
        footerFormat: '</table>',
        shared: true,
        useHTML: true
      },
      series: series
    };

    const comparisonSpecificOptions = {
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
        itemStyle: { fontWeight: 'normal' },
        itemMarginTop: 5,
        itemMarginBottom: 5
      },
      plotOptions: {
        column: {
          pointPadding: 0.1,
          groupPadding: 0.05,
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            inside: true,
            align: 'center',
            verticalAlign: 'middle',
            rotation: categories.length > 20 ? 270 : 0,
            format: '{point.y:.1f}',
            style: {
              textOutline: 'none'
            },
            formatter: function() {
              const barColor = this.point.color || this.series.color;
              const textColor = getContrastTextColor(barColor);

              return `<span style="color: ${textColor}">${this.y?.toFixed(1)}</span>`;
            }
          }
        },
        series: {
          marker: {
            symbol: 'circle',
            fillColor: null,
            lineWidth: 1,
            lineColor: "#CCCCCC"
          },
          states: {
            hover: { brightness: 0.1 }
          }
        }
      },
      series: series,
      drilldown: {
        allowPointDrilldown: false,
        activeAxisLabelStyle: {
          cursor: 'default',
          color: 'rgb(51, 51, 51)',
          textDecoration: 'none',
          fontWeight: 500,
          tickmarkPlacement: categories.length > 10 ? 'on' : 'between',
          tickWidth: 1
        },
        activeDataLabelStyle: {
          cursor: 'default',
          color: 'rgb(51, 51, 51)',
          textDecoration: 'none',
        },
        series: []
      },
      exporting: {
        enabled: showHamburger,
        ...chartExportableOptions
      }
    };

    const nonComparisonSpecificOptions = {
      legend: {
        align: 'center',
        verticalAlign: 'top',
        layout: 'horizontal',
        itemStyle: { fontWeight: 'normal' },
        itemMarginTop: 5,
        itemMarginBottom: 5
      },
      plotOptions: {
        column: {
          pointPadding: 0.1,
          groupPadding: 0.05,
          rotation: categories.length > 20 ? 270 : 0,
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            inside: true,
            align: 'center',
            verticalAlign: 'middle',
            rotation: categories.length > 20 ? 270 : 0,
            formatter: function() {
              const barColor = this.point.color || this.series.color;
              const textColor = getContrastTextColor(barColor);

              return `<span style="color: ${textColor}">${this.y?.toFixed(1)}</span>`;
            },
            style: {
              textOutline: 'none'
            }
          }
        },
        series: {
          marker: {
            symbol: 'circle',
            fillColor: null,
            lineWidth: 1,
            lineColor: "#CCCCCC"
          },
          states: {
            hover: { brightness: 0.1 }
          }
        }
      },
      drilldown: {
        allowPointDrilldown: false,
        activeAxisLabelStyle: {
          cursor: 'default',
          color: 'rgb(51, 51, 51)',
          textDecoration: 'none',
          fontWeight: 500,
          tickmarkPlacement: categories.length > 10 ? 'on' : 'between',
          tickWidth: 1
        },
        activeDataLabelStyle: {
          cursor: 'default',
          color: 'rgb(51, 51, 51)',
          textDecoration: 'none',
        },
        series: []
      },
      exporting: {
        enabled: showHamburger,
        ...chartExportableOptions
      }
    };

    return {
      ...baseOptions,
      ...(isComparison ? comparisonSpecificOptions : nonComparisonSpecificOptions)
    };
  }

  function createDrilldownSeries(data, selectedPointName, index, metrics, colors = null, enableDrillDown = false) {
    const positions = [...new Set(data.map(item => item.player_id__position))];

    const drilldowns = {};
    let names = []; 
    // Second layer: Positions
    metrics.forEach((metricKey, metricKeyIdx) => {
      const seriesName = metricKey.key
      drilldowns[seriesName] = {
        name: 'Positions',
        id: `${metricKey.key}-${selectedPointName}-${index}`,
        data: positions.map(position => {
          const positionData = data.filter(item => item.player_id__position === position);
          const avgValue = healthyMean(positionData, item => item[metricKey.key], injuredInGroup);
          return {
            name: position,
            y: avgValue === null ? null : Number(avgValue.toFixed(2)),
            color:  teamColors[metricKeyIdx],
            ...(enableDrillDown && {drilldown: `${metricKey.key}-${position}-${selectedPointName}-${index}`})
          };
        })
      };
    });

    // Third layer: Players
    metrics.forEach((metricKey, metricKeyIdx) => {
      positions.forEach(position => {
        const positionSeriesName = `${position} Players - ${metricKey.key}`;
        const playersData = data
          .filter(item => item.player_id__position === selectedPointName)
          .map(player => {
            const playerName = drilldownPlayerLabel(player);
            names.push(playerName);  

            return {
              name: playerName,
              y: Number(player[metricKey.key]?.toFixed(2)),
              custom: { injured: injuredInGroup(player) },
              color: teamColors[metricKeyIdx],
            };
          });

        drilldowns[positionSeriesName] = {
          name: 'Players',
          id: `${metricKey.key}-${position}-${selectedPointName}-${index}`,
          data: playersData
        };
      });
    });
    return {drilldowns, positions, names};
  }

  return options;
}

export function mapDataForMultiCompareBarChart(props) {
  const {
    series,
    categories,
    title,
    isCumulative = false,
    decimal_places = 1,
    showHamburger = true
  } = props;
  
  if (!series === 0) {
    return null;
  }

  const options = {
    chart: {
      type: 'column',
      zoomType: 'xy'
    },
    title: { text: title },
    legend: {
      align: 'center',
      verticalAlign: 'top',
      layout: 'horizontal',
      itemStyle: { fontWeight: 'normal' },
      itemMarginTop: 5,
      itemMarginBottom: 5
    },
    xAxis: {
      categories,
      labels: categories.length > 20
      ? {
          rotation: -90,
          overflow: 'justify',
          staggerLines: 1,
          step: 0
        }
      : categories.length > 10
      ? {
          rotation: -45,
          overflow: 'justify',
          staggerLines: 1,
          step: 0
        }
      : {
          rotation: 0,
        },
      tickmarkPlacement: 'between',
      gridLineWidth: 2,
      crosshair: true
    },
    yAxis: {
      min: 0,
      gridLineWidth: 0,
      title: { text: null }
    },
    tooltip: {
      headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
      pointFormat: '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
        (isCumulative ? '<td style="padding:0"><b>{point.rawValue}</b></td></tr>' : '<td style="padding:0"><b>{point.y:.' + decimal_places + 'f}</b></td></tr>'),
      footerFormat: '</table>',
      shared: true,
      useHTML: true
    },
    plotOptions: {
      column: {
        pointPadding: 0.1,
        groupPadding: 0.05,
        rotation: categories.length > 20 ? 270 : 0,
        borderWidth: 0,
        dataLabels: {
          enabled: true,
          inside: true,
          align: 'center',
          verticalAlign: 'middle',
          rotation: categories.length > 20 ? 270 : 0,
          formatter: function() {
            const barColor = this.point.color || this.series.color;
            const textColor = getContrastTextColor(barColor);
            if (isCumulative) {
              return `<span style="color: ${textColor}">${this.point?.rawValue?.toFixed ? this.point?.rawValue?.toFixed(decimal_places) : this.point?.rawValue}</span>`;
            }
            return `<span style="color: ${textColor}">${this.y?.toFixed(decimal_places)}</span>`;
          },
          style: {
            textOutline: isCumulative ? '1px contrast' :'none'
          }
        }
      },
      series: {
        marker: {
          symbol: 'circle',
          fillColor: null,
          lineWidth: 1,
          lineColor: "#CCCCCC"
        },
        states: {
          hover: { brightness: 0.1 }
        }
      }
    },
    series,
    exporting: {
      enabled: showHamburger,
      buttons: {
        contextButton: {
          menuItems: [
              'viewFullscreen',
              'printChart',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG'
          ]
        }
      },
      menuItemDefinitions: {
          // Customize existing menu items
          viewFullscreen: {
              onclick: function () {
                  this.fullscreen.toggle();
              }
          },
          printChart: {
              onclick: function () {
                const categories = this.options.custom?.currentCategories;
                  if (categories) {
                      this.update({
                          xAxis: {
                              categories: categories
                          }
                      });
                  }
                  this.print();
              }
          },
          downloadPNG: {
              onclick: function () {
                  const categories = this.options.custom?.currentCategories;
                  const drilldown = this.options.custom?.drilldown;
                  const title = this.options.custom?.title.toLowerCase().replace(/\s+/g, '_');
                  let filename = `${title}${(drilldown ? `_${drilldown}_drilldown`: '')}`;
                  
                  if (categories) {
                      this.update({
                          xAxis: {
                              categories: categories
                          }
                      });
                  }

                  this.exportChartLocal({
                      type: 'image/png',
                      filename
                  });

                  this.update({
                      xAxis: {
                          type: 'category'
                      }
                  });
                }
          },
          downloadJPEG: {
              onclick: function () {
                  const categories = this.options.custom?.currentCategories;
                  const drilldown = this.options.custom?.drilldown;
                  const title = this.options.custom?.title.toLowerCase().replace(/\s+/g, '_');
                  let filename = `${title}${(drilldown ? `_${drilldown}_drilldown`: '')}`;

                  if (categories) {
                      this.update({
                          xAxis: {
                              categories: categories
                          }
                      });
                  }

                  this.exportChartLocal({
                    type: 'image/jpeg',
                    filename
                  });

                  this.update({
                      xAxis: {
                          type: 'category'
                      }
                  });
              }
          },
          downloadPDF: {
              onclick: function () {
                  const categories = this.options.custom?.currentCategories;
                  const drilldown = this.options.custom?.drilldown;
                  const title = this.options.custom?.title.toLowerCase().replace(/\s+/g, '_');
                  let filename = `${title}${(drilldown ? `_${drilldown}_drilldown`: '')}`;

                  if (categories) {
                      this.update({
                          xAxis: {
                              categories: categories
                          }
                      });
                  }

                  this.exportChartLocal({
                    type: 'application/pdf',
                    filename
                  });

                  this.update({
                      xAxis: {
                          type: 'category'
                      }
                  });
              }
          },
          downloadSVG: {
              onclick: function () {
                  const categories = this.options.custom?.currentCategories;
                  const drilldown = this.options.custom?.drilldown;
                  const title = this.options.custom?.title.toLowerCase().replace(/\s+/g, '_');
                  let filename = `${title}${(drilldown ? `_${drilldown}_drilldown`: '')}`;

                  if (categories) {
                      this.update({
                          xAxis: {
                              categories: categories
                          }
                      });
                  }

                  this.exportChartLocal({
                    type: 'image/svg+xml',
                    filename
                  });

                  this.update({
                      xAxis: {
                          type: 'category'
                      }
                  });
              }
          }
      }
    }
  };

  return options;
}
